import {
  buildInstanceBatches,
  buildFloorLineBatches,
  buildFloorLineBatchesIndexed,
  buildZoneFills,
  buildZoneFillsIndexed,
  CompactInstanceElement,
  FloorPolylineInput,
  InstanceBatchResult,
  FloorLineBatchResult,
  ZoneFillResult,
  WORKER_FLOOR_THRESHOLD,
  WORKER_INSTANCE_THRESHOLD,
} from './scene3dCompute';

import type { WorkerRequest, WorkerResponse } from './scene3dMessages';

type Pending = {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
};

class Scene3DWorkerClient {
  private worker: Worker | null = null;
  private nextId = 0;
  private pending = new Map<number, Pending>();
  private workerFailed = false;

  private ensureWorker(): Worker | null {
    if (this.workerFailed || typeof Worker === 'undefined') return null;

    if (!this.worker) {
      try {
        this.worker = new Worker(new URL('./scene3d.worker.ts', import.meta.url), {
          type: 'module',
        });
        this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
          const data = event.data;
          const entry = this.pending.get(data.id);
          if (!entry) return;
          this.pending.delete(data.id);

          if ('error' in data && data.error) {
            entry.reject(new Error(data.error));
            return;
          }
          entry.resolve(data);
        };
        this.worker.onerror = () => {
          this.workerFailed = true;
          this.worker?.terminate();
          this.worker = null;
          for (const [, entry] of this.pending) {
            entry.reject(new Error('3D worker crashed'));
          }
          this.pending.clear();
        };
      } catch {
        this.workerFailed = true;
        return null;
      }
    }

    return this.worker;
  }

  private request<T extends WorkerResponse>(msg: Omit<WorkerRequest, 'id'>): Promise<T> {
    const worker = this.ensureWorker();
    if (!worker) {
      return Promise.reject(new Error('Web Worker unavailable'));
    }

    const id = ++this.nextId;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      worker.postMessage({ ...msg, id });
    });
  }

  async computeInstanceBatches(
    items: CompactInstanceElement[],
    useWorker: boolean
  ): Promise<InstanceBatchResult[]> {
    if (!useWorker || items.length < WORKER_INSTANCE_THRESHOLD) {
      return buildInstanceBatches(items);
    }

    try {
      const res = await this.request<Extract<WorkerResponse, { type: 'buildInstanceBatches' }>>({
        type: 'buildInstanceBatches',
        payload: { items },
      });
      return res.batches;
    } catch {
      return buildInstanceBatches(items);
    }
  }

  async computeFloorBatchesIndexed(
    polylines: number[][],
    polylineLayers: number[],
    layerNames: string[],
    layerStrokeColors: string[],
    polylineStrokeColors: string[],
    hiddenLayers: string[],
    visible: boolean,
    darkMode: boolean
  ): Promise<{ lineBatches: FloorLineBatchResult[]; zoneFills: ZoneFillResult[] }> {
    const hidden = new Set(hiddenLayers);
    const useWorker = polylines.length >= WORKER_FLOOR_THRESHOLD;

    if (!useWorker) {
      return {
        lineBatches: buildFloorLineBatchesIndexed(
          polylines,
          polylineLayers,
          layerNames,
          hidden,
          visible,
          darkMode,
          layerStrokeColors,
          polylineStrokeColors
        ),
        zoneFills: buildZoneFillsIndexed(polylines, polylineLayers, layerNames, hidden, visible),
      };
    }

    try {
      const res = await this.request<Extract<WorkerResponse, { type: 'buildFloorBatches' }>>({
        type: 'buildFloorBatchesIndexed',
        payload: {
          polylines,
          polylineLayers,
          layerNames,
          layerStrokeColors,
          polylineStrokeColors,
          hiddenLayers,
          visible,
          darkMode,
        },
      });
      return { lineBatches: res.lineBatches, zoneFills: res.zoneFills };
    } catch {
      return {
        lineBatches: buildFloorLineBatchesIndexed(
          polylines,
          polylineLayers,
          layerNames,
          hidden,
          visible,
          darkMode,
          layerStrokeColors,
          polylineStrokeColors
        ),
        zoneFills: buildZoneFillsIndexed(polylines, polylineLayers, layerNames, hidden, visible),
      };
    }
  }

  async computeFloorBatches(
    polylines: FloorPolylineInput[],
    hiddenLayers: string[],
    visible: boolean,
    darkMode: boolean,
    useWorker: boolean
  ): Promise<{ lineBatches: FloorLineBatchResult[]; zoneFills: ZoneFillResult[] }> {
    const hidden = new Set(hiddenLayers);
    if (!useWorker || polylines.length < WORKER_FLOOR_THRESHOLD) {
      return {
        lineBatches: buildFloorLineBatches(polylines, hidden, visible, darkMode),
        zoneFills: buildZoneFills(polylines, hidden, visible),
      };
    }

    try {
      const res = await this.request<Extract<WorkerResponse, { type: 'buildFloorBatches' }>>({
        type: 'buildFloorBatches',
        payload: { polylines, hiddenLayers, visible, darkMode },
      });
      return { lineBatches: res.lineBatches, zoneFills: res.zoneFills };
    } catch {
      return {
        lineBatches: buildFloorLineBatches(polylines, hidden, visible, darkMode),
        zoneFills: buildZoneFills(polylines, hidden, visible),
      };
    }
  }

  async generateLayout3DWithBatches(payload: {
    bounds: { minX: number; minY: number; maxX: number; maxY: number };
    scaleFactor: number;
    objectCount: number;
    boundsKey: string;
    cachedProject?: import('../utils/layout3dGenerate').Layout3DProject;
  }): Promise<{
    project: import('../utils/layout3dGenerate').Layout3DProject;
    batches: InstanceBatchResult[];
  }> {
    try {
      const res = await this.request<
        Extract<WorkerResponse, { type: 'generateLayout3DWithBatches' }>
      >({
        type: 'generateLayout3DWithBatches',
        payload,
      });
      return { project: res.project, batches: res.batches };
    } catch {
      const { generateLayout3DProject } = await import('../utils/layout3dGenerate');
      const { layoutElementsToCompact } = await import('./layout3dSerialize');
      const project =
        payload.cachedProject ??
        generateLayout3DProject(
          payload.bounds,
          payload.scaleFactor,
          payload.objectCount,
          payload.boundsKey
        );
      const batches = buildInstanceBatches(
        layoutElementsToCompact(project.elements, project.elementOrder)
      );
      return { project, batches };
    }
  }

  terminate(): void {
    this.worker?.terminate();
    this.worker = null;
    this.pending.clear();
  }
}

export const scene3dWorkerClient = new Scene3DWorkerClient();

export { WORKER_INSTANCE_THRESHOLD, WORKER_FLOOR_THRESHOLD };
